import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, Check, ChevronLeft, ChevronRight, MapPin, Send, Sparkles, Store, Sun, Tag, Wrench } from 'lucide-react';
import { ApiError, api, VENDOR_BASE } from './api';
import { UserErrorNotice } from './UserErrorNotice';

type Lang = 'en' | 'ur';
type City = { id: string; name: string };
type Area = { id: string; name: string; city_id?: string };
type Brand = { id: number; name: string; slug: string; component_type: string };
type Service = { id: number; name: string; category: string };

type DealEntry = {
  system_type: string;
  system_size_kw: string;
  deal_price: string;
  normal_price: string;
  show_normal_price: boolean;
  installments_available: boolean;
  vendor_down_payment: string;
  vendor_tenure_months: string;
  vendor_monthly_installment: string;
  inclusion_codes: string[];
  image_url: string;
  image_display_url: string;
};

type ProjectEntry = {
  project_type: string;
  system_size_kw: string;
  installation_year: string;
  image_url: string;
  image_display_url: string;
};

const BRAND_GROUPS = [
  { key: 'INVERTER_BATTERY', types: ['INVERTER', 'BATTERY'] },
  { key: 'PANEL', types: ['PANEL', 'STRUCTURE'] },
];

const SYSTEM_TYPES = [
  { value: 'ON_GRID', label: 'On-Grid Solar' },
  { value: 'HYBRID', label: 'Hybrid Solar' },
  { value: 'OFF_GRID', label: 'Off-Grid Solar' },
  { value: 'UPS_BACKUP', label: 'UPS / Backup' },
];

const SYSTEM_SIZES = ['1', '2', '3', '5', '6', '10', '15', 'OTHER'];

const PROJECT_TYPES = [
  { value: 'RESIDENTIAL', label: 'Ghar' },
  { value: 'SHOP', label: 'Shop' },
  { value: 'OFFICE', label: 'Office' },
  { value: 'FACTORY', label: 'Factory' },
];

const INCLUSIONS = [
  { value: 'SOLAR_PANELS', label: 'Solar Panels' },
  { value: 'INVERTER', label: 'Inverter' },
  { value: 'BATTERY', label: 'Battery' },
  { value: 'STRUCTURE', label: 'Structure' },
  { value: 'WIRING', label: 'Wiring' },
  { value: 'INSTALLATION', label: 'Installation' },
];

const num = (value: string): number | null => (value.trim() ? Number(value) : null);

const emptyDeal = (): DealEntry => ({
  system_type: 'HYBRID',
  system_size_kw: '5',
  deal_price: '',
  normal_price: '',
  show_normal_price: false,
  installments_available: false,
  vendor_down_payment: '',
  vendor_tenure_months: '',
  vendor_monthly_installment: '',
  inclusion_codes: ['SOLAR_PANELS', 'INVERTER', 'STRUCTURE', 'WIRING', 'INSTALLATION'],
  image_url: '',
  image_display_url: '',
});

const emptyProject = (): ProjectEntry => ({
  project_type: 'RESIDENTIAL',
  system_size_kw: '5',
  installation_year: String(new Date().getFullYear()),
  image_url: '',
  image_display_url: '',
});

const copy = {
  en: {
    eyebrow: 'Solar Partner Program',
    title: 'Join the Munshi-Gee Solar Network',
    body: 'One modern application. Our team reviews every detail before your activation link goes out on WhatsApp.',
    business: 'Business details',
    businessHint: 'Owner and business information.',
    ownerName: 'Owner / contact name',
    businessName: 'Business name',
    whatsapp: 'WhatsApp number',
    primaryPhone: 'Other phone (optional)',
    email: 'Email (optional)',
    primaryCity: 'Primary city',
    pickCity: 'City',
    officeAddress: 'Shop / office address',
    website: 'Website (optional)',
    year: 'Year established (optional)',
    description: 'Short business description (optional)',
    services: 'Services',
    servicesTitle: 'Services You Provide',
    servicesHint: 'Select the solar and electrical services customers can contact you for.',
    areas: 'Service areas',
    areasHint: 'Pick a city, then choose all areas or specific areas.',
    addCity: 'Add city coverage',
    allAreas: 'Cover all areas of this city',
    specificAreas: 'Choose specific areas',
    covered: 'Coverage',
    none: 'None selected',
    brands: 'Brands',
    brandsTitle: 'Brands You Work With',
    brandsHint: 'Select the solar panel, inverter and battery brands you supply or install.',
    inverters: 'Inverters & Battery Storage',
    panels: 'Solar PV Panels & Electrical',
    projects: 'Projects',
    projectsTitle: 'Add Your Previous Work',
    projectsHint: 'Show customers the solar systems you have already installed. Photos of your completed work build trust and can help you get more quote requests.',
    projectType: 'Project type',
    projectSize: 'System size (kW)',
    projectYear: 'Installation year',
    addProject: 'Add another project',
    deals: 'Deals',
    dealsTitle: 'Add Your Best Deals',
    dealsHint: 'Add competitive packages so Munshi-Gee can show them to customers looking for a system like yours.',
    dealType: 'Package type',
    dealSize: 'System size (kW)',
    customerPrice: 'Munshi-Gee Deal Price (PKR)',
    customerPriceHint: 'Your special price for customers coming through Munshi-Gee.',
    marketPrice: 'Regular Price (PKR) — Optional',
    marketPriceHint: 'Enter your normal selling price so customers can see how much they save with this deal.',
    showMarketPrice: 'Show Regular Price & Saving',
    installments: 'Available in installments',
    downPayment: 'Down payment (PKR)',
    tenure: 'Tenure (months)',
    monthly: 'Monthly installment (PKR)',
    included: 'Items included in this price',
    image: 'Image',
    addImage: 'Upload image',
    addDeal: 'Add another deal',
    consent: 'I confirm the details are accurate and allow Munshi-Gee to review and display approved public details.',
    submit: 'Submit application',
    submitting: 'Submitting…',
    next: 'Continue',
    back: 'Back',
    review: 'Review & submit',
    stepOf: 'Step',
    required: 'Please fill your name, business name and WhatsApp number.',
    successTitle: 'Application received',
    successBody: 'Our team will review your details and send an activation link on WhatsApp if approved.',
    reference: 'Your reference',
    optionsError: 'Could not load the form options. Please refresh the page.',
  },
  ur: {
    eyebrow: 'سولر پارٹنر پروگرام',
    title: 'منشی جی سولر نیٹ ورک میں شامل ہوں',
    body: 'ایک جدید درخواست۔ منظوری کے بعد آپ کو واٹس ایپ پر ایکٹیویشن لنک بھیجا جائے گا۔',
    business: 'کاروبار کی تفصیلات',
    businessHint: 'مالک اور کاروبار کی معلومات۔',
    ownerName: 'مالک / رابطہ نام',
    businessName: 'کاروبار کا نام',
    whatsapp: 'واٹس ایپ نمبر',
    primaryPhone: 'دوسرا نمبر (اختیاری)',
    email: 'ای میل (اختیاری)',
    primaryCity: 'مرکزی شہر',
    pickCity: 'شہر',
    officeAddress: 'دکان / دفتر کا پتہ',
    website: 'ویب سائٹ (اختیاری)',
    year: 'قائم ہونے کا سال (اختیاری)',
    description: 'مختصر کاروباری تعارف (اختیاری)',
    services: 'سروسز',
    servicesTitle: 'آپ کی فراہم کردہ خدمات',
    servicesHint: 'وہ سولر اور الیکٹریکل خدمات منتخب کریں جن کے لیے صارف آپ سے رابطہ کر سکیں۔',
    areas: 'سروس کے علاقے',
    areasHint: 'شہر منتخب کریں، پھر تمام یا مخصوص علاقے چنیں۔',
    addCity: 'شہر کی کوریج شامل کریں',
    allAreas: 'اس شہر کے تمام علاقے',
    specificAreas: 'مخصوص علاقے منتخب کریں',
    covered: 'کوریج',
    none: 'کچھ منتخب نہیں',
    brands: 'برانڈز',
    brandsTitle: 'آپ جن برانڈز پر کام کرتے ہیں',
    brandsHint: 'سولر پینل، انورٹر اور بیٹری برانڈز منتخب کریں جو آپ فراہم یا انسٹال کرتے ہیں۔',
    inverters: 'انورٹرز اور بیٹری اسٹوریج',
    panels: 'سولر پی وی پینلز اور الیکٹریکل',
    projects: 'پراجیکٹس',
    projectsTitle: 'اپنا پچھلا کام شامل کریں',
    projectsHint: 'صارفین کو وہ سولر سسٹم دکھائیں جو آپ پہلے انسٹال کر چکے ہیں۔ مکمل شدہ کام کی تصاویر اعتماد بناتی ہیں اور زیادہ کوٹ درخواستیں لا سکتی ہیں۔',
    projectType: 'پراجیکٹ کی قسم',
    projectSize: 'سسٹم سائز (kW)',
    projectYear: 'انسٹالیشن کا سال',
    addProject: 'ایک اور پراجیکٹ شامل کریں',
    deals: 'ڈیلز',
    dealsTitle: 'اپنی بہترین ڈیلز شامل کریں',
    dealsHint: 'مسابقتی پیکجز شامل کریں تاکہ منشی جی انہیں آپ جیسے سسٹم تلاش کرنے والے صارفین کو دکھا سکے۔',
    dealType: 'پیکج کی قسم',
    dealSize: 'سسٹم سائز (kW)',
    customerPrice: 'منشی جی ڈیل قیمت (PKR)',
    customerPriceHint: 'منشی جی سے آنے والے صارفین کے لیے آپ کی خصوصی قیمت۔',
    marketPrice: 'ریگولر قیمت (PKR) — اختیاری',
    marketPriceHint: 'اپنی عام فروخت قیمت درج کریں تاکہ صارفین دیکھ سکیں کہ اس ڈیل سے کتنی بچت ہو رہی ہے۔',
    showMarketPrice: 'ریگولر قیمت اور بچت دکھائیں',
    installments: 'اقساط میں دستیاب',
    downPayment: 'ڈاؤن پیمنٹ (PKR)',
    tenure: 'مدت (مہینے)',
    monthly: 'ماہانہ قسط (PKR)',
    included: 'اس قیمت میں شامل اشیاء',
    image: 'تصویر',
    addImage: 'تصویر اپ لوڈ کریں',
    addDeal: 'ایک اور ڈیل شامل کریں',
    consent: 'میں تصدیق کرتا ہوں کہ تفصیلات درست ہیں اور منشی جی ان کی جانچ اور منظوری شدہ عوامی تفصیلات ظاہر کر سکتا ہے۔',
    submit: 'درخواست جمع کریں',
    submitting: 'جمع کیا جا رہا ہے…',
    next: 'اگلا',
    back: 'واپس',
    review: 'جائزہ اور جمع',
    stepOf: 'مرحلہ',
    required: 'براہ کرم نام، کاروبار کا نام اور واٹس ایپ نمبر درج کریں۔',
    successTitle: 'درخواست موصول ہوئی',
    successBody: 'ہماری ٹیم تفصیلات دیکھے گی اور منظوری پر واٹس ایپ پر ایکٹیویشن لنک بھیجے گی۔',
    reference: 'آپ کا حوالہ نمبر',
    optionsError: 'فارم کے اختیارات لوڈ نہیں ہو سکے۔ براہ کرم صفحہ ریفریش کریں۔',
  },
} as const;


const STEPS = [
  { key: 'business', icon: Store },
  { key: 'services', icon: Wrench },
  { key: 'areas', icon: MapPin },
  { key: 'brands', icon: Sun },
  { key: 'projects', icon: Briefcase },
  { key: 'deals', icon: Tag },
  { key: 'review', icon: Send },
];

export function VendorApplyExperience({ lang }: { lang: Lang }) {
  const t = copy[lang];
  const stepLabels = [t.business, t.services, t.areas, t.brands, t.projects, t.deals, t.review];

  const [step, setStep] = useState(0);
  const [cities, setCities] = useState<City[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ reference: string; status: string; business_name: string; message: string } | null>(null);

  const [ownerName, setOwnerName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [email, setEmail] = useState('');
  const [primaryCityId, setPrimaryCityId] = useState('');
  const [officeAddress, setOfficeAddress] = useState('');
  const [website, setWebsite] = useState('');
  const [yearEstablished, setYearEstablished] = useState('');
  const [description, setDescription] = useState('');

  const [serviceIds, setServiceIds] = useState<number[]>([]);
  const [brandIds, setBrandIds] = useState<number[]>([]);

  const [selectedCityId, setSelectedCityId] = useState('');
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [coversAllCityIds, setCoversAllCityIds] = useState<string[]>([]);

  const [deals, setDeals] = useState<DealEntry[]>([emptyDeal()]);
  const [projects, setProjects] = useState<ProjectEntry[]>([emptyProject()]);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async (path: string, set: (v: any) => void) => {
      try {
        const data = await api<any[]>(path, { base: VENDOR_BASE, auth: false });
        if (!cancelled) set(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) set([]);
      }
    };
    load('/api/v1/core/cities', setCities);
    load('/api/v1/solar/brands', setBrands);
    load('/api/v1/public/vendor/service-catalogue', setServices);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedCityId) { setAreas([]); return; }
    let cancelled = false;
    api<Area[]>(`/api/v1/core/cities/${encodeURIComponent(selectedCityId)}/areas`, { base: VENDOR_BASE, auth: false })
      .then((a) => { if (!cancelled) setAreas(Array.isArray(a) ? a : []); })
      .catch(() => { if (!cancelled) setAreas([]); });
    return () => { cancelled = true; };
  }, [selectedCityId]);

  const groupedBrands = useMemo(() => BRAND_GROUPS.map((group) => ({
    ...group,
    brands: brands.filter((b) => group.types.includes(String(b.component_type || '').toUpperCase())),
  })), [brands]);

  const toggleId = (list: number[], setList: (v: number[]) => void, id: number) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };
  const toggleArea = (id: string) => {
    setAreaIds(areaIds.includes(id) ? areaIds.filter((x) => x !== id) : [...areaIds, id]);
  };
  const selectedCityCoversAll = coversAllCityIds.includes(selectedCityId);
  const toggleSelectedCityCoversAll = () => {
    if (!selectedCityId) return;
    setCoversAllCityIds(
      selectedCityCoversAll
        ? coversAllCityIds.filter((id) => id !== selectedCityId)
        : [...coversAllCityIds, selectedCityId],
    );
  };

  const updateDeal = (index: number, patch: Partial<DealEntry>) => {
    setDeals(deals.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };
  const toggleDealInclusion = (index: number, code: string) => {
    const deal = deals[index];
    updateDeal(index, {
      inclusion_codes: deal.inclusion_codes.includes(code)
        ? deal.inclusion_codes.filter((c) => c !== code)
        : [...deal.inclusion_codes, code],
    });
  };
  const updateProject = (index: number, patch: Partial<ProjectEntry>) => {
    setProjects(projects.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  const [uploadingImage, setUploadingImage] = useState(false);
  const uploadImage = async (file: File, onDone: (url: string, display: string) => void) => {
    setUploadingImage(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const result = await api<{ image_url: string; display_url: string }>('/api/v1/public/vendor/images', {
        base: VENDOR_BASE, auth: false, method: 'POST', body: fd,
      });
      onDone(result.image_url, result.display_url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (lang === 'ur' ? 'تصویر اپ لوڈ نہیں ہو سکی۔' : 'Image upload failed.'));
    } finally {
      setUploadingImage(false);
    }
  };

  const submit = async () => {
    if (!ownerName.trim() || !businessName.trim() || !whatsapp.trim()) {
      setError(t.required);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        business_name: businessName.trim(),
        owner_name: ownerName.trim(),
        whatsapp_phone: whatsapp.trim(),
        primary_phone: primaryPhone.trim() || null,
        email: email.trim() || null,
        primary_city_id: primaryCityId || null,
        office_address: officeAddress.trim() || null,
        website: website.trim() || null,
        year_established: num(yearEstablished),
        business_description: description.trim() || null,
        service_ids: serviceIds,
        brand_ids: brandIds,
        area_ids: areaIds,
        covers_all_city_ids: coversAllCityIds,
        deals: deals
          .filter((d) => d.deal_price || d.normal_price)
          .map((d) => ({
            system_type: d.system_type,
            system_size_kw: d.system_size_kw === 'OTHER' ? null : num(d.system_size_kw),
            deal_price: num(d.deal_price),
            normal_price: num(d.normal_price),
            show_normal_price: d.show_normal_price,
            installments_available: d.installments_available,
            vendor_down_payment: num(d.vendor_down_payment),
            vendor_tenure_months: d.vendor_tenure_months ? Math.round(num(d.vendor_tenure_months) as number) : null,
            vendor_monthly_installment: num(d.vendor_monthly_installment),
            inclusion_codes: d.inclusion_codes,
            image_url: d.image_url || null,
          })),
        projects: projects
          .filter((p) => p.system_size_kw && p.system_size_kw !== 'OTHER')
          .map((p) => ({
            project_type: p.project_type,
            system_size_kw: num(p.system_size_kw),
            installation_year: num(p.installation_year) ? Math.round(num(p.installation_year) as number) : null,
            image_url: p.image_url || null,
          })),
        consent,
        source: 'WEB_URL',
        company_website: '',
      };
      const result = await api<{ reference: string; status: string; business_name: string; message: string }>(
        '/api/v1/public/vendor/applications',
        { base: VENDOR_BASE, auth: false, method: 'POST', body: JSON.stringify(payload) },
      );
      setSubmitted(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (lang === 'ur' ? 'درخواست جمع نہیں ہو سکی۔' : 'Application could not be submitted.'));
    } finally {
      setLoading(false);
    }
  };

  const isLast = step === STEPS.length - 1;
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isLast) { setStep(step + 1); return; }
    void submit();
  };


  if (submitted) {
    return (
      <div className="vw-shell">
        <div className="vw-success">
          <div className="vw-success-icon"><Check /></div>
          <h2>{t.successTitle}</h2>
          <p><b>{submitted.business_name}</b></p>
          <p>{submitted.message}</p>
          <p className="vw-ref-label">{t.reference}</p>
          <div className="vw-reference">{submitted.reference}</div>
        </div>
      </div>
    );
  }

  const StepIcon = STEPS[step].icon;

  return (
    <div className="vw-shell">
      <div className="vw-hero">
        <span className="vw-hero-pill"><Sparkles size={13} /> {t.eyebrow}</span>
        <h1>{t.title}</h1>
        <p>{t.body}</p>
      </div>

      <div className="vw-progress">
        <div className="vw-progress-label">
          <span>{t.stepOf} {step + 1} / {STEPS.length}</span>
          <b>{stepLabels[step]}</b>
        </div>
        <div className="vw-steps">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`vw-step${i < step ? ' done' : i === step ? ' active' : ''}`}>
              <span className="vw-step-dot">{i < step ? <Check size={15} /> : i + 1}</span>
              <span className="vw-step-label">{stepLabels[i]}</span>
            </div>
          ))}
        </div>
      </div>

      <UserErrorNotice error={error} lang={lang} />

      <form className="vw-form" onSubmit={onSubmit}>
        {step === 0 && (
          <section className="vw-card">
            <div className="vw-card-head"><span className="vw-icon"><StepIcon size={20} /></span><h2>{t.business}</h2></div>
            <p className="vw-hint">{t.businessHint}</p>
            <div className="vw-grid">
              <label className="vw-field"><span>{t.ownerName} *</span><input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} autoComplete="name" /></label>
              <label className="vw-field"><span>{t.businessName} *</span><input value={businessName} onChange={(e) => setBusinessName(e.target.value)} autoComplete="organization" /></label>
              <label className="vw-field"><span>{t.whatsapp} *</span><input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} inputMode="tel" dir="ltr" placeholder="+92 3xx xxxxxxx" /></label>
              <label className="vw-field"><span>{t.primaryPhone}</span><input value={primaryPhone} onChange={(e) => setPrimaryPhone(e.target.value)} inputMode="tel" dir="ltr" /></label>
              <label className="vw-field"><span>{t.email}</span><input value={email} onChange={(e) => setEmail(e.target.value)} type="email" /></label>
              <label className="vw-field"><span>{t.primaryCity}</span>
                <select value={primaryCityId} onChange={(e) => setPrimaryCityId(e.target.value)}>
                  <option value="">—</option>
                  {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="vw-field vw-full"><span>{t.officeAddress}</span><input value={officeAddress} onChange={(e) => setOfficeAddress(e.target.value)} /></label>
              <label className="vw-field"><span>{t.website}</span><input value={website} onChange={(e) => setWebsite(e.target.value)} dir="ltr" /></label>
              <label className="vw-field"><span>{t.year}</span><input value={yearEstablished} onChange={(e) => setYearEstablished(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" /></label>
              <label className="vw-field vw-full"><span>{t.description}</span><textarea value={description} onChange={(e) => setDescription(e.target.value)} /></label>
            </div>
          </section>
        )}

        {step === 1 && (
          <section className="vw-card">
            <div className="vw-card-head"><span className="vw-icon"><StepIcon size={20} /></span><h2>{t.servicesTitle}</h2></div>
            <p className="vw-hint">{t.servicesHint}</p>
            <div className="vw-chips">
              {services.map((s) => (
                <button type="button" key={s.id} className={`vw-chip${serviceIds.includes(s.id) ? ' on' : ''}`} onClick={() => toggleId(serviceIds, setServiceIds, s.id)}>{s.name}</button>
              ))}
            </div>
          </section>
        )}
        {step === 2 && (
          <section className="vw-card">
            <div className="vw-card-head"><span className="vw-icon"><StepIcon size={20} /></span><h2>{t.areas}</h2></div>
            <p className="vw-hint">{t.areasHint}</p>
            <label className="vw-field">
              <span>{t.pickCity}</span>
              <select value={selectedCityId} onChange={(e) => setSelectedCityId(e.target.value)}>
                <option value="">—</option>
                {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            {selectedCityId && (
              <div className="vw-area-picker">
                <label className="vw-toggle-row">
                  <span>{t.allAreas}</span>
                  <button type="button" className={`vw-toggle${selectedCityCoversAll ? ' on' : ''}`} onClick={toggleSelectedCityCoversAll} aria-pressed={selectedCityCoversAll}>
                    <span className="vw-toggle-knob" />
                  </button>
                </label>
                {!selectedCityCoversAll && (
                  <div className="vw-chips">
                    {areas.length ? areas.map((a) => (
                      <button type="button" key={a.id} className={`vw-chip${areaIds.includes(a.id) ? ' on' : ''}`} onClick={() => toggleArea(a.id)}>{a.name}</button>
                    )) : <span className="vw-hint">{t.none}</span>}
                  </div>
                )}
              </div>
            )}
            {(coversAllCityIds.length > 0 || areaIds.length > 0) && (
              <div className="vw-coverage">
                <b>{t.covered}:</b>
                {coversAllCityIds.map((id) => <span className="vw-tag" key={`c-${id}`}>{cities.find((c) => c.id === id)?.name || id} · all</span>)}
                {areaIds.map((id) => <span className="vw-tag" key={`a-${id}`}>{areas.find((a) => a.id === id)?.name || id}</span>)}
              </div>
            )}
          </section>
        )}

        {step === 3 && (
          <section className="vw-card">
            <div className="vw-card-head"><span className="vw-icon"><StepIcon size={20} /></span><h2>{t.brandsTitle}</h2></div>
            <p className="vw-hint">{t.brandsHint}</p>
            {groupedBrands.map((group) => (
              <div className="vw-brand-group" key={group.key}>
                <h3>{group.key === 'PANEL' ? t.panels : t.inverters}</h3>
                <div className="vw-chips">
                  {group.brands.length ? group.brands.map((b) => (
                    <button type="button" key={b.id} className={`vw-chip${brandIds.includes(b.id) ? ' on' : ''}`} onClick={() => toggleId(brandIds, setBrandIds, b.id)}>{b.name}</button>
                  )) : <span className="vw-hint">{t.none}</span>}
                </div>
              </div>
            ))}
          </section>
        )}

        {step === 4 && (
          <section className="vw-card">
            <div className="vw-card-head"><span className="vw-icon"><StepIcon size={20} /></span><h2>{t.projectsTitle}</h2></div>
            <p className="vw-hint">{t.projectsHint}</p>
            {projects.map((p, i) => (
              <div className="vw-entry" key={i}>
                <span className="vw-entry-title">#{i + 1}</span>
                <div className="vw-grid">
                  <label className="vw-field"><span>{t.projectType}</span>
                    <select value={p.project_type} onChange={(e) => updateProject(i, { project_type: e.target.value })}>
                      {PROJECT_TYPES.map((pt) => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                    </select>
                  </label>
                  <label className="vw-field"><span>{t.projectSize}</span>
                    <select value={p.system_size_kw} onChange={(e) => updateProject(i, { system_size_kw: e.target.value })}>
                      {SYSTEM_SIZES.map((s) => <option key={s} value={s}>{s === 'OTHER' ? 'Other' : `${s} kW`}</option>)}
                    </select>
                  </label>
                  <label className="vw-field"><span>{t.projectYear}</span>
                    <input value={p.installation_year} onChange={(e) => updateProject(i, { installation_year: e.target.value.replace(/\D/g, '').slice(0, 4) })} inputMode="numeric" />
                  </label>
                </div>
                <div className="vw-field">
                  <span>{t.image}</span>
                  {p.image_display_url ? (
                    <div className="vw-image-preview">
                      <img src={p.image_display_url} alt="" />
                      <button type="button" className="vw-remove-img" onClick={() => updateProject(i, { image_url: '', image_display_url: '' })}>✕</button>
                    </div>
                  ) : (
                    <label className="vw-file-btn">
                      {uploadingImage ? '…' : t.addImage}
                      <input type="file" accept="image/png,image/jpeg,image/webp" disabled={uploadingImage}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f, (u, d) => updateProject(i, { image_url: u, image_display_url: d })); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
                {projects.length > 1 && <button type="button" className="vw-remove" onClick={() => setProjects(projects.filter((_, x) => x !== i))}>✕</button>}
              </div>
            ))}
            <button type="button" className="vw-add" onClick={() => setProjects([...projects, emptyProject()])}>+ {t.addProject}</button>
          </section>
        )}
        {step === 5 && (
          <section className="vw-card">
            <div className="vw-card-head"><span className="vw-icon"><StepIcon size={20} /></span><h2>{t.dealsTitle}</h2></div>
            <p className="vw-hint">{t.dealsHint}</p>
            {deals.map((d, i) => (
              <div className="vw-entry" key={i}>
                <span className="vw-entry-title">#{i + 1}</span>
                <div className="vw-grid">
                  <label className="vw-field"><span>{t.dealType}</span>
                    <select value={d.system_type} onChange={(e) => updateDeal(i, { system_type: e.target.value })}>
                      {SYSTEM_TYPES.map((st) => <option key={st.value} value={st.value}>{st.label}</option>)}
                    </select>
                  </label>
                  <label className="vw-field"><span>{t.dealSize}</span>
                    <select value={d.system_size_kw} onChange={(e) => updateDeal(i, { system_size_kw: e.target.value })}>
                      {SYSTEM_SIZES.map((s) => <option key={s} value={s}>{s === 'OTHER' ? 'Other' : `${s} kW`}</option>)}
                    </select>
                  </label>
                  <label className="vw-field"><span>{t.customerPrice}</span><input value={d.deal_price} onChange={(e) => updateDeal(i, { deal_price: e.target.value })} inputMode="numeric" placeholder="PKR" /><small className="vw-field-hint">{t.customerPriceHint}</small></label>
                  <label className="vw-field"><span>{t.marketPrice}</span><input value={d.normal_price} onChange={(e) => updateDeal(i, { normal_price: e.target.value })} inputMode="numeric" placeholder="PKR" /><small className="vw-field-hint">{t.marketPriceHint}</small></label>
                </div>

                <label className="vw-toggle-row">
                  <span>{t.showMarketPrice}</span>
                  <button type="button" className={`vw-toggle${d.show_normal_price ? ' on' : ''}`} onClick={() => updateDeal(i, { show_normal_price: !d.show_normal_price })} aria-pressed={d.show_normal_price}>
                    <span className="vw-toggle-knob" />
                  </button>
                </label>

                <label className="vw-toggle-row">
                  <span>{t.installments}</span>
                  <button type="button" className={`vw-toggle${d.installments_available ? ' on' : ''}`} onClick={() => updateDeal(i, { installments_available: !d.installments_available })} aria-pressed={d.installments_available}>
                    <span className="vw-toggle-knob" />
                  </button>
                </label>

                {d.installments_available && (
                  <div className="vw-grid">
                    <label className="vw-field"><span>{t.downPayment}</span><input value={d.vendor_down_payment} onChange={(e) => updateDeal(i, { vendor_down_payment: e.target.value })} inputMode="numeric" /></label>
                    <label className="vw-field"><span>{t.tenure}</span><input value={d.vendor_tenure_months} onChange={(e) => updateDeal(i, { vendor_tenure_months: e.target.value })} inputMode="numeric" /></label>
                    <label className="vw-field"><span>{t.monthly}</span><input value={d.vendor_monthly_installment} onChange={(e) => updateDeal(i, { vendor_monthly_installment: e.target.value })} inputMode="numeric" /></label>
                  </div>
                )}

                <div className="vw-field">
                  <span>{t.included}</span>
                  <div className="vw-chips">
                    {INCLUSIONS.map((inc) => (
                      <button type="button" key={inc.value} className={`vw-chip${d.inclusion_codes.includes(inc.value) ? ' on' : ''}`} onClick={() => toggleDealInclusion(i, inc.value)}>{inc.label}</button>
                    ))}
                  </div>
                </div>

                <div className="vw-field">
                  <span>{t.image}</span>
                  {d.image_display_url ? (
                    <div className="vw-image-preview">
                      <img src={d.image_display_url} alt="" />
                      <button type="button" className="vw-remove-img" onClick={() => updateDeal(i, { image_url: '', image_display_url: '' })}>✕</button>
                    </div>
                  ) : (
                    <label className="vw-file-btn">
                      {uploadingImage ? '…' : t.addImage}
                      <input type="file" accept="image/png,image/jpeg,image/webp" disabled={uploadingImage}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f, (u, d) => updateDeal(i, { image_url: u, image_display_url: d })); e.target.value = ''; }} />
                    </label>
                  )}
                </div>

                {deals.length > 1 && <button type="button" className="vw-remove" onClick={() => setDeals(deals.filter((_, x) => x !== i))}>✕</button>}
              </div>
            ))}
            <button type="button" className="vw-add" onClick={() => setDeals([...deals, emptyDeal()])}>+ {t.addDeal}</button>
          </section>
        )}


        {step === 6 && (
          <section className="vw-card">
            <div className="vw-card-head"><span className="vw-icon"><StepIcon size={20} /></span><h2>{t.review}</h2></div>
            <div className="vw-summary">
              <div className="vw-summary-row"><b>{t.businessName}</b><span>{businessName || '—'}</span></div>
              <div className="vw-summary-row"><b>{t.whatsapp}</b><span dir="ltr">{whatsapp || '—'}</span></div>
              <div className="vw-summary-row"><b>{t.primaryCity}</b><span>{cities.find((c) => c.id === primaryCityId)?.name || '—'}</span></div>
              <div className="vw-summary-row"><b>{t.services}</b><span>{serviceIds.length}</span></div>
              <div className="vw-summary-row"><b>{t.brands}</b><span>{brandIds.length}</span></div>
              <div className="vw-summary-row"><b>{t.areas}</b><span>{coversAllCityIds.length + areaIds.length}</span></div>
              <div className="vw-summary-row"><b>{t.projects}</b><span>{projects.filter((p) => p.system_size_kw && p.system_size_kw !== 'OTHER').length}</span></div>
              <div className="vw-summary-row"><b>{t.deals}</b><span>{deals.filter((d) => d.deal_price || d.normal_price).length}</span></div>
            </div>
            <label className="vw-consent" style={{ marginTop: 16 }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>{t.consent}</span>
            </label>
          </section>
        )}

        <div className="vw-nav">
          {step > 0 && <button type="button" className="vw-back" onClick={() => setStep(step - 1)}><ChevronLeft size={16} /> {t.back}</button>}
          {!isLast ? (
            <button type="button" className="vw-next" onClick={() => setStep(step + 1)}>{t.next} <ChevronRight size={16} /></button>
          ) : (
            <button type="submit" className="vw-next" disabled={loading || !consent}>{loading ? t.submitting : t.submit}</button>
          )}
        </div>
      </form>
    </div>
  );
}
